import { useDispatch, useSelector } from "react-redux";
import { setErrors } from "../../../store/alerts/alertsActions";
import { useEffect, useMemo, useState } from "react";
import { get, post } from "../../../api";
import useFacility from "../../../../utils/hooks/useFacility";
import { GET_BASKET_QB, SAVE_BASKET_QB } from "../../../api/apiRoutes";
import { QbBasketData } from "../../../models/QnBasketData";
import { State } from "../../../store";
import { BasketItem } from "../../../models/BasketItem";
import { UNKNOWN_ERROR } from "../../../api/tryCatchRequest";

/** Indeksy pozycji koszyka ze zduplikowanym numerem karty (ten sam trimowany `qb_card` w więcej niż jednym wierszu). */
export function getDuplicateQbCardIndices(basket: BasketItem[] | undefined): Set<number> {
  if (!basket?.length) return new Set();
  const cardToIndices = new Map<string, number[]>();
  basket.forEach((item, idx) => {
    const card = (item.qb_card ?? "").trim();
    if (!card) return;
    const list = cardToIndices.get(card);
    if (list) list.push(idx);
    else cardToIndices.set(card, [idx]);
  });
  const dup = new Set<number>();
  cardToIndices.forEach((indices) => {
    if (indices.length > 1) indices.forEach((i) => dup.add(i));
  });
  return dup;
}

/* eslint-disable import/no-anonymous-default-export */
export default (onClose: () => void) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<QbBasketData>({} as QbBasketData);
  const dispatch = useDispatch();
  const facilityIds = useFacility();
  const basketLength = useSelector((state: State) => state.basket.basketLength);

  const getBasketQb = async () => {
    setLoading(true);
    const { result, isError, errorTable } = await get(GET_BASKET_QB(facilityIds));
    if (!isError) {
      setData(result);
    } else {
      dispatch(setErrors(errorTable));
    }
    setLoading(false);
  };

  useEffect(() => {
    getBasketQb();
    //eslint-disable-next-line
  }, []);

  const duplicateQbCardIndices = useMemo(() => getDuplicateQbCardIndices(data.basket), [data.basket]);
  const hasDuplicateQbCards = duplicateQbCardIndices.size > 0;
  const isQbRowDuplicateCard = (index: number) => duplicateQbCardIndices.has(index);

  const updateBasketItem = (index: number, key: "qb_card" | "qb_pin", value: string) => {
    const updatedBasket = [...data.basket];
    updatedBasket[index] = { ...updatedBasket[index], [key]: value };
    setData({ ...data, basket: updatedBasket });
  };

  const onUpdateBasket = async () => {
    if (hasDuplicateQbCards) return;
    setLoading(true);
    const { result, isError, errorTable } = await post(SAVE_BASKET_QB(facilityIds), { basket: data.basket });
    if (!isError) {
      if (result) {
        const hasInvalidDiscount = result.some(
          (item: BasketItem) => item.qb_znizka_id <= 0 && Boolean(item.qb_message)
        );
        if (hasInvalidDiscount) {
          setData({ ...data, basket: result });
        } else {
          setData({} as QbBasketData);
          onClose();
        }
      } else {
        dispatch(setErrors([UNKNOWN_ERROR]));
      }
    } else {
      dispatch(setErrors(errorTable));
    }
    setLoading(false);
  };

  return {
    loading,
    data,
    updateBasketItem,
    onUpdateBasket,
    basketLength,
    hasDuplicateQbCards,
    isQbRowDuplicateCard,
  };
};
